import React, { useEffect, useRef, useState } from 'react';
import { ApiError, api } from '@/lib/api';

/**
 * Контекстное меню по ПКМ на ячейке журнала.
 *
 * Логика:
 *   • 5/4/3/2/Н вводятся напрямую с клавиатуры — здесь не нужны.
 *   • «К» (комментарий) выставляется только из этого меню — выбор из справочника
 *     grade_comments. score='К', comment=текст комментария (поэтому на hover
 *     ячейки показывается tooltip).
 *   • Опоздание — выпадайка из late_ranges, для всех классов.
 *   • Свободная заметка — отдельное поле для оценочного режима (3–4); в
 *     режиме комментариев (0–2) комментарий и есть содержание ячейки.
 *   • Кнопка «Удалить» — посылает пустую отметку, бэк сам сносит запись.
 */
export default function MarkPopover({
  anchorRect, studentId, classId, classNumber, subjectId, scheduleLessonId,
  date, existing, gradeComments, lateRanges,
  onClose, onSaved,
}) {
  const isCommentsMode = classNumber !== null && classNumber !== undefined && classNumber <= 2;

  const initialPresence = existing ? existing.presence : true;
  const initialScore    = existing?.score ?? '';
  const initialLate     = existing?.late ?? '';
  const initialComment  = existing?.comment ?? '';

  const [late, setLate]       = useState(initialLate);
  const [comment, setComment] = useState(initialComment);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // Универсальный апдейт. Все поля шлём явно — иначе бэк примет, что ничего не
  // прислали, и применит дефолты.
  const send = async (overrides = {}) => {
    setSaving(true); setError(null);
    try {
      const payload = {
        studentId, classId, subjectId, scheduleLessonId,
        date, type: 'daily',
        score:    initialScore,
        presence: initialPresence ? 1 : 0,
        late, comment,
        ...overrides,
      };
      const res = await api.put('/api/journal/mark', payload);
      onSaved?.(res.mark);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err.message || 'Не удалось сохранить'));
      setSaving(false);
    }
  };

  // Клик по комментарию: score='К', comment = текст комментария
  const pickComment = (c) => send({ score: 'К', comment: c.text, presence: 1 });
  // «Н» прямо из меню (для удобства — то же даёт клавиша Н)
  const setAbsent  = () => send({ score: '', comment: '', late: '', presence: 0 });
  // Удалить отметку — пустые поля, бэкенд сделает DELETE
  const remove = () => {
    if (!existing) { onClose(); return; }
    if (!confirm('Удалить отметку?')) return;
    send({ score: '', comment: '', late: '', presence: 1 });
  };
  // «Применить» (только late/comment изменили без оценки)
  const applyMeta = () => send();

  // Позиционирование: рядом с курсором, но не за правым краем
  const top  = (anchorRect?.top || 0) + window.scrollY;
  const left = Math.min(
    (anchorRect?.left || 0) + window.scrollX,
    window.innerWidth - 320
  );

  return (
    <div
      ref={ref}
      className="jr-popover"
      style={{ position: 'absolute', top, left, zIndex: 200, width: 320 }}
    >
      {isCommentsMode && (
        <>
          <div className="jr-popover__menuTitle">Комментарий</div>
          <div className="jr-popover__commentList">
            {(gradeComments || []).filter((c) => !c.archive).map((c) => {
              const selected = initialPresence && initialScore === 'К' && initialComment === c.text;
              return (
                <button
                  key={c.id}
                  type="button"
                  className={'jr-popover__commentBtn' + (selected ? ' is-on' : '')}
                  onClick={() => pickComment(c)}
                  disabled={saving}
                  title={c.text}
                >
                  <span
                    className="jr-popover__commentPill"
                    style={{ background: c.color || 'var(--panel-2)' }}
                  >К</span>
                  <span className="jr-popover__commentText">{c.text}</span>
                </button>
              );
            })}
          </div>
          <div className="jr-popover__sep" />
        </>
      )}

      <button
        type="button"
        className={'jr-popover__commentBtn jr-popover__commentBtn--absent' + (!initialPresence ? ' is-on' : '')}
        onClick={setAbsent}
        disabled={saving}
        title="Отсутствовал"
      >
        <span className="jr-popover__commentPill jr-c-absent">Н</span>
        <span className="jr-popover__commentText">Отсутствовал</span>
      </button>

      <div className="jr-popover__row" style={{ gap: 6, alignItems: 'center', marginTop: 6 }}>
        <span style={{ fontSize: 12, color: 'var(--fg-muted)', flexShrink: 0 }}>Опоздание:</span>
        <select
          value={late}
          onChange={(e) => setLate(e.target.value)}
          disabled={saving || !initialPresence}
          className="jr-popover__input"
          style={{ flex: 1 }}
        >
          <option value="">— нет —</option>
          {(lateRanges || []).filter((r) => !r.archive).map((r) => (
            <option key={r.id} value={r.label}>{r.label}</option>
          ))}
          {late && !(lateRanges || []).some((r) => r.label === late) && (
            <option value={late}>{late} (устаревшее)</option>
          )}
        </select>
      </div>

      {!isCommentsMode && (
        <input
          type="text"
          placeholder="Заметка (необязательно)"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          className="jr-popover__input"
          disabled={saving}
        />
      )}

      {error && <div className="jr-popover__error">{error}</div>}

      <div className="jr-popover__foot">
        {existing && (
          <button
            type="button"
            className="btn btn--sm btn--danger"
            onClick={remove}
            disabled={saving}
            style={{ marginRight: 'auto' }}
          >
            Удалить
          </button>
        )}
        <button type="button" className="btn btn--sm" onClick={onClose} disabled={saving}>Закрыть</button>
        <button type="button" className="btn btn--primary btn--sm" onClick={applyMeta} disabled={saving}>
          {saving ? '…' : 'Применить'}
        </button>
      </div>
    </div>
  );
}
